'use client';

import { Suspense, lazy, useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { IconPlayerPlayFilled, IconAlertCircle } from '@tabler/icons-react';
import { Button } from '@headlessui/react';
import { cn } from '@/app/utils/cn';
import VideoLoadingIndicator from '@/components/shared/VideoLoadingIndicator';

const ReactPlayer = lazy(() => import('react-player/youtube'));

function YouTube(props) {
    const {
        thumbnail,
        fullWidth = true,
        imagePrimaryLoad,
        paragraphSpacing = 'nospace',
        positionCenter = false
    } = props;

    const attrs = props.node?.attrs ?? props.node ?? props.content?.attrs ?? props.attrs ?? {};

    const src = attrs.src ?? props.src ?? props.srcYoutube;
    const width = Math.max(Number(attrs.width ?? props.width ?? props.widthYoutube) || 16, 1);
    const height = Math.max(Number(attrs.height ?? props.height ?? props.heightYoutube) || 9, 1);
    const start = attrs.start ?? props.start ?? 0;
    const loop = attrs.loop ?? props.loop ?? props.loopYoutube ?? false;
    const shouldAutoplay = attrs.autoplay ?? props.autoplay ?? props.autoplayYoutube ?? false;

    const [playerState, setPlayerState] = useState({
        videoPlaying: false,
        isPlayerReady: false,
        isPlayerLoading: false,
        shouldLoadPlayer: false,
        playerError: null,
        retryCount: 0
    });

    const loadingTimeoutRef = useRef(null);

    const getYouTubeId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[2] && match[2].length === 11 ? match[2] : null;
    };

    const videoId = getYouTubeId(src);

    const getInitialThumbnail = () => {
        if (thumbnail) return thumbnail;
        if (!videoId) return null;
        const size = Math.max(width, height) >= 1280 ? 'maxresdefault' : 'sddefault';
        return `https://img.youtube.com/vi_webp/${videoId}/${size}.webp`;
    };

    const [imgSrc, setImgSrc] = useState(getInitialThumbnail());

    useEffect(() => {
        setImgSrc(getInitialThumbnail());
    }, [thumbnail, videoId, width, height]);

    useEffect(() => {
        if (shouldAutoplay) {
            togglePlayPause();
        }
    }, [shouldAutoplay]);

    // Safety timeout to clear loading state if player never fires onReady/onError
    useEffect(() => {
        if (playerState.isPlayerLoading && !playerState.isPlayerReady) {
            loadingTimeoutRef.current = setTimeout(() => {
                setPlayerState((prev) => ({
                    ...prev,
                    isPlayerLoading: false,
                    playerError: 'Loading timeout'
                }));
            }, 8000);
        } else {
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        }
        return () => {
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        };
    }, [playerState.isPlayerLoading, playerState.isPlayerReady]);

    const handleImageError = () => {
        if (!videoId || typeof imgSrc !== 'string') return;
        if (imgSrc.includes('maxresdefault')) {
            setImgSrc(`https://img.youtube.com/vi_webp/${videoId}/sddefault.webp`);
        } else if (imgSrc.includes('sddefault')) {
            setImgSrc(`https://img.youtube.com/vi_webp/${videoId}/hqdefault.webp`);
        }
    };

    const togglePlayPause = () => {
        setPlayerState((prev) => {
            if (!prev.videoPlaying || prev.playerError) {
                return {
                    ...prev,
                    shouldLoadPlayer: true,
                    isPlayerLoading: !prev.isPlayerReady,
                    videoPlaying: true,
                    playerError: null
                };
            }
            return { ...prev, videoPlaying: false };
        });
    };

    const handlePlayerError = (error) => {
        console.error('YouTube Player Error:', error);
        setPlayerState((prev) => ({
            ...prev,
            isPlayerLoading: false,
            playerError: 'Unable to load video'
        }));
    };

    const containerStyle = {
        aspectRatio: fullWidth ? '16 / 9' : `${width} / ${height}`,
        maxWidth: fullWidth ? '100%' : `${width > 100 ? width : 560}px`
    };

    if (!src || !isYouTubeUrl(src)) {
        return (
            <div
                className={cn(
                    'w-full bg-gray-200 dark:bg-gray-800 rounded-lg flex items-center justify-center',
                    positionCenter && 'mx-auto'
                )}
                style={containerStyle}>
                <p className="text-sm text-gray-500">Invalid YouTube URL</p>
            </div>
        );
    }

    return (
        <div className={cn(paragraphSpacing !== 'nospace' && `spacing-${paragraphSpacing}`)}>
            <div
                className={cn(
                    'relative w-full overflow-hidden rounded-lg bg-black group',
                    positionCenter && 'mx-auto'
                )}
                style={containerStyle}>
                <Button className="relative block size-full text-left" onClick={togglePlayPause}>
                    {/* Poster Image / Play Button Overlay */}
                    {imgSrc &&
                        (!playerState.videoPlaying || !playerState.isPlayerReady) &&
                        !playerState.playerError && (
                            <>
                                <Image
                                    src={imgSrc}
                                    alt="Video thumbnail"
                                    fill
                                    priority={imagePrimaryLoad}
                                    className="object-cover"
                                    sizes="(max-width: 768px) 100vw, 1200px"
                                    onError={handleImageError}
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/20">
                                    <div className="flex size-16 items-center justify-center rounded-full bg-red-600 text-white shadow-xl transition-transform group-hover:scale-110">
                                        <IconPlayerPlayFilled className="ml-1 size-6" />
                                    </div>
                                </div>
                            </>
                        )}

                    {/* Error State UI */}
                    {playerState.playerError && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 p-6 text-center text-white">
                            <IconAlertCircle className="mb-2 size-10 text-red-500" />
                            <p className="mb-2 text-sm font-medium">{playerState.playerError}</p>
                            <p className="mb-4 text-xs text-gray-400">
                                This may be caused by an ad blocker or network issue.
                            </p>
                            <div className="flex gap-3">
                                <span className="rounded bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20">
                                    Try again
                                </span>
                                {videoId && (
                                    <a
                                        href={`https://www.youtube.com/watch?v=${videoId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="rounded bg-red-600 px-3 py-1.5 text-xs font-semibold hover:bg-red-700"
                                        onClick={(e) => e.stopPropagation()}>
                                        Watch on YouTube
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Actual Player */}
                    {playerState.shouldLoadPlayer && !playerState.playerError && (
                        <Suspense fallback={<VideoLoadingIndicator />}>
                            <ReactPlayer
                                url={src}
                                width="100%"
                                height="100%"
                                playing={playerState.videoPlaying}
                                loop={loop}
                                onReady={() =>
                                    setPlayerState((p) => ({
                                        ...p,
                                        isPlayerReady: true,
                                        isPlayerLoading: false
                                    }))
                                }
                                onError={handlePlayerError}
                                config={{
                                    youtube: {
                                        playerVars: { rel: 0, modestbranding: 1, start }
                                    }
                                }}
                                style={{ position: 'absolute', top: 0, left: 0 }}
                            />
                        </Suspense>
                    )}

                    {/* Loading Indicator */}
                    {playerState.isPlayerLoading &&
                        playerState.videoPlaying &&
                        !playerState.playerError && <VideoLoadingIndicator />}
                </Button>
            </div>
        </div>
    );
}

function isYouTubeUrl(url) {
    return (
        url && /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com)\/.+/.test(url)
    );
}

export default YouTube;
